import { useMemo, useState } from 'react'
import { Button, Select, Space, Switch, Typography } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { Can } from '@/shared/auth/Can'
import { PageHeader } from '@/shared/ui/PageHeader'
import { PurchaseFormModal } from '@/features/supplier/PurchaseFormModal'
import { PurchasesTable } from '@/features/supplier/PurchasesTable'
import { PURCHASE_STATES, fetchActiveSuppliers, type PurchaseState } from '@/features/supplier/supplierApi'

export function PurchasesPage() {
  const { t } = useTranslation()
  const [supplierId, setSupplierId] = useState<string | null>(null)
  const [state, setState] = useState<PurchaseState | null>(null)
  const [overdueOnly, setOverdueOnly] = useState(false)
  const [creating, setCreating] = useState(false)

  const suppliers = useQuery({ queryKey: queryKeys.supplier.active, queryFn: ({ signal }) => fetchActiveSuppliers(signal) })
  const filter = useMemo(() => ({ supplierId, state, overdueOnly }), [supplierId, state, overdueOnly])

  return (
    <>
      <PageHeader
        title={t('nav.purchases')}
        description={t('pageIntro.purchases')}
        extra={
          <Can permission="purchase.create">
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreating(true)}>
              {t('purchase.action.create')}
            </Button>
          </Can>
        }
      />
      <Space wrap style={{ marginBottom: 16 }}>
        <Select<string>
          allowClear
          showSearch
          optionFilterProp="label"
          style={{ minWidth: 240 }}
          placeholder={t('purchase.allSuppliers')}
          value={supplierId ?? undefined}
          onChange={(value) => setSupplierId(value ?? null)}
          loading={suppliers.isPending}
          options={(suppliers.data ?? []).map((s) => ({ value: s.id, label: `${s.name} · ${s.code}` }))}
        />
        <Select<PurchaseState | 'ALL'>
          style={{ minWidth: 200 }}
          value={state ?? 'ALL'}
          onChange={(value) => setState(value === 'ALL' ? null : value)}
          options={[
            { value: 'ALL', label: t('purchase.allStates') },
            ...PURCHASE_STATES.map((value) => ({ value, label: t(`purchase.state.${value}`) })),
          ]}
        />
        <Space>
          <Switch checked={overdueOnly} onChange={setOverdueOnly} />
          <Typography.Text>{t('purchase.overdueOnly')}</Typography.Text>
        </Space>
      </Space>
      <PurchasesTable filter={filter} tableId="purchases" />
      <PurchaseFormModal open={creating} purchase={null} onClose={() => setCreating(false)} onSaved={() => undefined} />
    </>
  )
}
